import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, Activity, BarChart2, Layers } from "lucide-react";
import { chainFreshness, formatAge, rowIsStale } from "@/lib/chainFreshness";
import ChainSourceBadge from "@/components/ChainSourceBadge";
import { OPTION_INDEX_SYMBOLS, OPTION_STOCK_SYMBOLS } from "@/lib/optionChain";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const STRATEGIES = [
  {
    id: "bull_call_spread",
    name: "Bull Call Spread",
    sentiment: "BULLISH",
    description: "Buy lower-strike call, sell higher-strike call. Limited profit, limited loss.",
    legs: (atm, step) => [
      { side: "BUY", kind: "CE", strike: atm, label: "Buy ATM Call" },
      { side: "SELL", kind: "CE", strike: atm + step, label: `Sell +${step} Call` },
    ],
  },
  {
    id: "bear_put_spread",
    name: "Bear Put Spread",
    sentiment: "BEARISH",
    description: "Buy higher-strike put, sell lower-strike put. Profit when market falls.",
    legs: (atm, step) => [
      { side: "BUY", kind: "PE", strike: atm, label: "Buy ATM Put" },
      { side: "SELL", kind: "PE", strike: atm - step, label: `Sell -${step} Put` },
    ],
  },
  {
    id: "straddle",
    name: "Long Straddle",
    sentiment: "NEUTRAL (VOLATILE)",
    description: "Buy ATM call + put. Profits when market moves big in either direction.",
    legs: (atm) => [
      { side: "BUY", kind: "CE", strike: atm, label: "Buy ATM Call" },
      { side: "BUY", kind: "PE", strike: atm, label: "Buy ATM Put" },
    ],
  },
  {
    id: "strangle",
    name: "Long Strangle",
    sentiment: "NEUTRAL (VOLATILE)",
    description: "Buy OTM call + OTM put. Cheaper than straddle, needs bigger move.",
    legs: (atm, step) => [
      { side: "BUY", kind: "CE", strike: atm + step, label: `Buy +${step} Call` },
      { side: "BUY", kind: "PE", strike: atm - step, label: `Buy -${step} Put` },
    ],
  },
  {
    id: "iron_condor",
    name: "Iron Condor",
    sentiment: "NEUTRAL (LOW VOL)",
    description: "Sell OTM call + put, buy further OTM call + put. Profit in range.",
    legs: (atm, step) => [
      { side: "SELL", kind: "CE", strike: atm + step, label: `Sell +${step} Call` },
      { side: "BUY",  kind: "CE", strike: atm + 2 * step, label: `Buy +${2*step} Call` },
      { side: "SELL", kind: "PE", strike: atm - step, label: `Sell -${step} Put` },
      { side: "BUY",  kind: "PE", strike: atm - 2 * step, label: `Buy -${2*step} Put` },
    ],
  },
  {
    id: "covered_call",
    name: "Covered Call",
    sentiment: "MILDLY BULLISH",
    description: "Hold stock/futures, sell OTM call. Generates income, caps upside.",
    legs: (atm, step) => [
      { side: "BUY",  kind: "FUTURES", strike: atm, label: "Hold underlying" },
      { side: "SELL", kind: "CE", strike: atm + step, label: `Sell +${step} Call` },
    ],
  },
  {
    id: "bull_put_spread",
    name: "Bull Put Spread",
    sentiment: "BULLISH",
    description: "Sell higher-strike put, buy lower-strike put. Collect premium, profit above upper strike.",
    legs: (atm, step) => [
      { side: "SELL", kind: "PE", strike: atm, label: "Sell ATM Put" },
      { side: "BUY",  kind: "PE", strike: atm - step, label: `Buy -${step} Put` },
    ],
  },
  {
    id: "bear_call_spread",
    name: "Bear Call Spread",
    sentiment: "BEARISH",
    description: "Sell lower-strike call, buy higher-strike call. Collect premium, profit below lower strike.",
    legs: (atm, step) => [
      { side: "SELL", kind: "CE", strike: atm, label: "Sell ATM Call" },
      { side: "BUY",  kind: "CE", strike: atm + step, label: `Buy +${step} Call` },
    ],
  },
];

const SENTIMENT_COLOR = {
  "BULLISH": "txt-profit border-emerald-500/40",
  "BEARISH": "txt-loss border-red-500/40",
  "NEUTRAL (VOLATILE)": "text-purple-400 border-purple-500/40",
  "NEUTRAL (LOW VOL)": "text-blue-400 border-blue-500/40",
  "MILDLY BULLISH": "txt-profit border-emerald-400/40",
};

const TABS = [
  { id: "strategies", label: "Strategy Templates", icon: Layers },
  { id: "payoff", label: "P&L Payoff", icon: BarChart2 },
  { id: "sentiment", label: "IV & Sentiment", icon: Activity },
  { id: "greeks", label: "Greeks", icon: TrendingUp },
];

// RU-MCXCHAINQ MQ-7: the chain's quote time and age above the table; stale rows (and a stale chain) are marked,
// never hidden. Chains without freshness fields (index, stock, generated) render as before.
export function ChainFreshnessNote({ chain }) {
  const f = chainFreshness(chain);
  if (!f) return null;
  return (
    <div data-testid="chain-freshness" className={`mb-3 text-xs font-mono-data ${f.stale ? "text-amber-400" : "txt-muted"}`}>
      Quotes as of {f.quoteTime || "—"} · age {f.age}
      {f.stale && (
        <span data-testid="chain-stale-badge" className="ml-2 px-1.5 py-0.5 border border-amber-500/50 text-amber-400 font-bold">
          STALE{f.staleAfter ? ` (older than ${f.staleAfter})` : ""}
        </span>
      )}
      {f.cached && <span className="ml-2">last live chain: no fresh quote right now</span>}
    </div>
  );
}

export function GreeksTable({ chain }) {
  const withAge = chainFreshness(chain) != null;
  return (
    <div className="overflow-x-auto">
      <ChainFreshnessNote chain={chain} />
      <table className="w-full text-xs font-mono-data">
        <thead>
          <tr className="border-b border-[var(--border)]">
            <th className="overline px-3 py-2 text-right">CE OI</th>
            <th className="overline px-3 py-2 text-right">CE Δ</th>
            <th className="overline px-3 py-2 text-right">CE IV%</th>
            <th className="overline px-3 py-2 text-right">CE Price</th>
            <th className="overline px-3 py-2 text-center bg-[var(--bg-surface-2)]">Strike</th>
            <th className="overline px-3 py-2 text-left">PE Price</th>
            <th className="overline px-3 py-2 text-left">PE IV%</th>
            <th className="overline px-3 py-2 text-left">PE Δ</th>
            <th className="overline px-3 py-2 text-left">PE OI</th>
            {withAge && <th className="overline px-3 py-2 text-left">Age</th>}
          </tr>
        </thead>
        <tbody>
          {chain.rows.map((row) => {
            const isAtm = row.strike === chain.atm;
            const stale = rowIsStale(row, chain);
            return (
              <tr key={row.strike} data-testid={`chain-row-${row.strike}`} data-stale={stale ? "true" : "false"}
                  className={`border-b border-[var(--border)] ${stale ? "opacity-60" : ""} ${isAtm ? "bg-blue-500/10" : "hover:bg-[var(--bg-surface-2)]"}`}>
                <td className="px-3 py-2 text-right txt-muted">{(row.ce.oi / 1000).toFixed(0)}K</td>
                <td className="px-3 py-2 text-right txt-profit">{row.ce.delta?.toFixed(2) ?? "—"}</td>
                <td className="px-3 py-2 text-right txt-secondary">{row.ce.iv?.toFixed(1)}%</td>
                <td className="px-3 py-2 text-right text-white font-bold">₹{fmt(row.ce.price)}</td>
                <td className={`px-3 py-2 text-center font-bold ${isAtm ? "text-blue-400" : "text-white"}`}>
                  {isAtm ? "★ " : ""}{row.strike}
                </td>
                <td className="px-3 py-2 text-left text-white font-bold">₹{fmt(row.pe.price)}</td>
                <td className="px-3 py-2 text-left txt-secondary">{row.pe.iv?.toFixed(1)}%</td>
                <td className="px-3 py-2 text-left txt-loss">{row.pe.delta?.toFixed(2) ?? "—"}</td>
                <td className="px-3 py-2 text-left txt-muted">{(row.pe.oi / 1000).toFixed(0)}K</td>
                {withAge && (
                  <td className={`px-3 py-2 text-left ${stale ? "text-amber-400 font-bold" : "txt-muted"}`}>
                    {formatAge(row.age_s)}{stale ? " · stale" : ""}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Options() {
  const [tab, setTab] = useState("strategies");
  const [symbol, setSymbol] = useState("NIFTY");
  const [chain, setChain] = useState(null);
  const [ivRank, setIvRank] = useState(null);
  const [pcr, setPcr] = useState(null);
  const [maxPain, setMaxPain] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [payoffData, setPayoffData] = useState([]);
  const [chainLoading, setChainLoading] = useState(false);
  const [chainError, setChainError] = useState(false);

  const loadData = useCallback(async () => {
    if (!symbol) return;
    setChainLoading(true);
    setChainError(false);
    // RU-OPTUI OU-2: no expiry_days: a live chain is always the active expiry, so the backend default is used.
    try {
      const [c, iv, p, mp] = await Promise.all([
        api.get("/market/options-chain", { params: { symbol } }),
        api.get(`/market/iv-rank/${symbol}`),
        api.get(`/market/pcr/${symbol}`),
        api.get(`/market/max-pain/${symbol}`),
      ]);
      setChain(c.data);
      setIvRank(iv.data);
      setPcr(p.data);
      setMaxPain(mp.data);
    } catch (e) {
      setChain(null);
      setChainError(true);
      toast.error("Failed to load options data");
    } finally {
      setChainLoading(false);
    }
  }, [symbol]);

  useEffect(() => { loadData(); }, [loadData]);

  // Build payoff diagram when strategy or chain changes
  useEffect(() => {
    if (!chain || !selectedStrategy) return;
    const atm = chain.atm;
    const step = chain.step;
    const rows = chain.rows;
    const strategy = STRATEGIES.find((s) => s.id === selectedStrategy);
    if (!strategy) return;

    const legs = strategy.legs(atm, step);
    const spots = [];
    const minSpot = atm - 10 * step;
    const maxSpot = atm + 10 * step;
    for (let s = minSpot; s <= maxSpot; s += step / 2) spots.push(s);

    const rowMap = {};
    rows.forEach((r) => {
      rowMap[r.strike] = r;
    });

    const data = spots.map((spot) => {
      let pnl = 0;
      for (const leg of legs) {
        if (leg.kind === "FUTURES") {
          pnl += (leg.side === "BUY" ? 1 : -1) * (spot - atm);
          continue;
        }
        const row = rowMap[leg.strike];
        if (!row) continue;
        const premium = row[leg.kind === "CE" ? "ce" : "pe"]?.price ?? 0;
        const intrinsic =
          leg.kind === "CE" ? Math.max(0, spot - leg.strike) : Math.max(0, leg.strike - spot);
        const expiryValue = intrinsic - premium;
        pnl += (leg.side === "BUY" ? 1 : -1) * expiryValue;
      }
      return { spot: Math.round(spot), pnl: Math.round(pnl * 100) / 100 };
    });
    setPayoffData(data);
  }, [chain, selectedStrategy]);

  const portfolioGreeks = (() => {
    if (!chain) return null;
    const atm = chain.atm;
    const rows = chain.rows;
    const totals = { delta: 0, gamma: 0, theta: 0, vega: 0 };
    // Show Greeks for ATM straddle as sample
    const atmRow = rows.find((r) => r.strike === atm);
    if (!atmRow) return null;
    ["ce", "pe"].forEach((kind) => {
      const g = atmRow[kind];
      totals.delta += g.delta ?? 0;
      totals.gamma += g.gamma ?? 0;
      totals.theta += g.theta ?? 0;
      totals.vega += g.vega ?? 0;
    });
    return totals;
  })();

  return (
    <AppShell>
      <PageHeader
        overline="Options"
        title="Options Intelligence"
        description="Strategy templates, P&L payoff diagrams, IV Rank, PCR, Max Pain, and portfolio Greeks."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">

        {/* Controls */}
        <div className="panel p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="overline">Symbol</Label>
            <Select value={symbol} onValueChange={setSymbol}>
              <SelectTrigger data-testid="options-symbol-select" className="mt-2 w-36 rounded-none bg-[var(--bg-page)] border-[var(--border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                <SelectGroup>
                  <SelectLabel className="overline">Indices</SelectLabel>
                  {OPTION_INDEX_SYMBOLS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel className="overline">Stocks</SelectLabel>
                  {OPTION_STOCK_SYMBOLS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {chain && (
            <div className="ml-auto font-mono-data text-sm">
              <span className="txt-muted">Spot:</span>{" "}
              <span className="font-bold">₹{fmt(chain.spot)}</span>
              <span className="txt-muted ml-4">ATM:</span>{" "}
              <span className="font-bold">₹{fmt(chain.atm)}</span>
              <span className="txt-muted ml-4">Expiry (from chain):</span>{" "}
              <span data-testid="options-chain-expiry">{chain.expiry || "—"}</span>
            </div>
          )}
          <div className={chain ? "w-full sm:w-auto" : "ml-auto"}>
            <ChainSourceBadge chain={chain} error={chainError} testid="options-chain-source" />
          </div>
        </div>

        {/* RU-OPTUI OU-4: a chain that is not live is illustrative only. RU-OPTFU OF-6: a stale_cache chain is the
            last real cached chain, named as such (RU-OPTSTALE OS-3b wording), never "Simulated" */}
        {chain && chain.source !== "live" && (
          <div data-testid="options-simulated-banner" className="panel p-3 border-red-500/50 txt-loss text-sm font-section tracking-wider">
            {chain.source === "stale_cache"
              ? "Stale cache — not tradable. This is the last cached chain, not current market data (stale_cache); premiums, "
              : `Simulated — not tradable. This chain is not live market data (${chain.source || "unknown source"}); premiums, `}
            OI, IV, PCR and max pain below are illustrative only.
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-[var(--border)] overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-section tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                tab === id
                  ? "border-white text-white"
                  : "border-transparent txt-muted hover:text-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* STRATEGY TEMPLATES */}
        {tab === "strategies" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {STRATEGIES.map((strat) => {
              const legs = chain ? strat.legs(chain.atm, chain.step) : [];
              const isSelected = selectedStrategy === strat.id;
              return (
                <div
                  key={strat.id}
                  onClick={() => { setSelectedStrategy(strat.id); setTab("payoff"); }}
                  className={`panel p-4 cursor-pointer transition-all hover:border-[var(--border-strong)] ${isSelected ? "border-white" : ""}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-section font-bold text-sm">{strat.name}</div>
                    <span className={`text-xs border px-1.5 py-0.5 ${SENTIMENT_COLOR[strat.sentiment] || "txt-muted border-[var(--border)]"}`}>
                      {strat.sentiment.split(" ")[0]}
                    </span>
                  </div>
                  <div className="txt-secondary text-xs mb-3">{strat.description}</div>
                  <div className="space-y-1">
                    {legs.map((leg, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-mono-data">
                        <span className={`font-section ${leg.side === "BUY" ? "txt-profit" : "txt-loss"}`}>{leg.side}</span>
                        <span className="txt-muted">{leg.strike ? `₹${leg.strike}` : ""}</span>
                        <span className="txt-secondary">{leg.kind}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs txt-muted">Click to see payoff →</div>
                </div>
              );
            })}
          </div>
        )}

        {/* P&L PAYOFF DIAGRAM */}
        {tab === "payoff" && (
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">P&L at Expiry</div>
                {selectedStrategy && (
                  <div className="font-section font-bold mt-0.5">
                    {STRATEGIES.find((s) => s.id === selectedStrategy)?.name}
                  </div>
                )}
              </div>
              <Select value={selectedStrategy || ""} onValueChange={setSelectedStrategy}>
                <SelectTrigger className="w-48 rounded-none bg-[var(--bg-page)] border-[var(--border)] text-xs">
                  <SelectValue placeholder="Select strategy…" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                  {STRATEGIES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!selectedStrategy && (
              <div className="h-64 flex items-center justify-center txt-muted text-sm">
                Select a strategy to see its payoff diagram.
              </div>
            )}
            {selectedStrategy && payoffData.length > 0 && (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={payoffData} margin={{ left: 16, right: 16 }}>
                    <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
                    <XAxis
                      dataKey="spot"
                      stroke="#52525b"
                      fontSize={10}
                      tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      stroke="#52525b"
                      fontSize={10}
                      tickFormatter={(v) => v >= 0 ? `+${v}` : `${v}`}
                    />
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                      formatter={(v) => [`₹${fmt(v)}`, "P&L"]}
                      labelFormatter={(v) => `Spot: ₹${fmt(v)}`}
                    />
                    <ReferenceLine y={0} stroke="#52525b" strokeDasharray="4 4" />
                    {chain && <ReferenceLine x={chain.spot} stroke="#3b82f6" strokeDasharray="4 4" label={{ value: "Current", fill: "#3b82f6", fontSize: 10 }} />}
                    <Line
                      type="monotone"
                      dataKey="pnl"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray={undefined}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {selectedStrategy && chain && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono-data">
                {(() => {
                  const maxPnl = payoffData.length ? Math.max(...payoffData.map((d) => d.pnl)) : 0;
                  const minPnl = payoffData.length ? Math.min(...payoffData.map((d) => d.pnl)) : 0;
                  const breakevens = payoffData.filter((d, i, arr) =>
                    i > 0 && ((arr[i - 1].pnl < 0 && d.pnl >= 0) || (arr[i - 1].pnl >= 0 && d.pnl < 0))
                  );
                  return (
                    <>
                      <div><div className="overline mb-1">Max Profit</div><div className="txt-profit text-lg">{maxPnl === Infinity ? "Unlimited" : `₹${fmt(maxPnl)}`}</div></div>
                      <div><div className="overline mb-1">Max Loss</div><div className="txt-loss text-lg">{minPnl === -Infinity ? "Unlimited" : `₹${fmt(Math.abs(minPnl))}`}</div></div>
                      <div><div className="overline mb-1">Breakeven(s)</div><div className="text-white">{breakevens.length ? breakevens.map((d) => `₹${fmt(d.spot)}`).join(", ") : "—"}</div></div>
                      <div><div className="overline mb-1">ATM</div><div className="text-white">₹{fmt(chain.atm)}</div></div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* IV & SENTIMENT */}
        {tab === "sentiment" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* IV Rank */}
            <div className="panel p-5">
              <div className="overline mb-4">IV Rank · {symbol}</div>
              {!ivRank ? <div className="txt-muted text-sm">Loading…</div> : ivRank.iv_rank == null ? (
                <div className="space-y-3" data-testid="iv-rank-no-history">
                  <div className="text-sm txt-secondary">Insufficient IV history — IV rank not available.</div>
                  <div className="text-xs font-mono-data">
                    <div className="overline">Current IV</div>
                    <div className="text-white mt-1">
                      {ivRank.current_iv}%
                      {ivRank.source ? <span className="ml-2 text-[10px] txt-muted">source: {ivRank.source === "mock" ? "simulated chain" : ivRank.source}</span> : null}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-3 mb-4">
                    <div className="font-display text-5xl font-bold">{ivRank.iv_rank}%</div>
                    <div className={`mb-1 text-sm font-section ${ivRank.regime === "HIGH" ? "txt-loss" : ivRank.regime === "LOW" ? "txt-profit" : "txt-secondary"}`}>
                      {ivRank.regime}
                    </div>
                  </div>
                  <div className="mb-3 h-2 bg-[var(--bg-surface-2)] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${ivRank.iv_rank > 70 ? "bg-red-500" : ivRank.iv_rank < 30 ? "bg-emerald-500" : "bg-yellow-500"}`}
                      style={{ width: `${Math.min(100, ivRank.iv_rank)}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs font-mono-data mb-4">
                    <div><div className="overline">Current IV</div><div className="text-white mt-1">{ivRank.current_iv}%</div></div>
                    <div><div className="overline">IV Percentile</div><div className="text-white mt-1">{ivRank.iv_percentile}%</div></div>
                    <div><div className="overline">52W Low</div><div className="txt-profit mt-1">{ivRank.iv_52w_low}%</div></div>
                    <div><div className="overline">52W High</div><div className="txt-loss mt-1">{ivRank.iv_52w_high}%</div></div>
                  </div>
                  <div className="text-xs txt-secondary border border-[var(--border)] p-3">
                    💡 {ivRank.strategy_hint}
                  </div>
                </>
              )}
            </div>

            {/* PCR */}
            <div className="panel p-5">
              <div className="overline mb-4">Put-Call Ratio · {symbol}</div>
              {!pcr ? <div className="txt-muted text-sm">Loading…</div> : (
                <>
                  <div className="flex items-end gap-3 mb-4">
                    <div className={`font-display text-5xl font-bold ${pcr.sentiment === "BULLISH" ? "txt-profit" : pcr.sentiment === "BEARISH" ? "txt-loss" : "txt-secondary"}`}>
                      {pcr.oi_pcr}
                    </div>
                    <div className={`mb-1 text-sm font-section ${pcr.sentiment === "BULLISH" ? "txt-profit" : pcr.sentiment === "BEARISH" ? "txt-loss" : "txt-secondary"}`}>
                      {pcr.sentiment}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs font-mono-data mb-4">
                    <div><div className="overline">OI PCR</div><div className="text-white mt-1">{pcr.oi_pcr}</div></div>
                    <div><div className="overline">Volume PCR</div><div className="text-white mt-1">{pcr.volume_pcr}</div></div>
                    <div><div className="overline">Call OI</div><div className="txt-profit mt-1">{(pcr.total_call_oi / 1e5).toFixed(1)}L</div></div>
                    <div><div className="overline">Put OI</div><div className="txt-loss mt-1">{(pcr.total_put_oi / 1e5).toFixed(1)}L</div></div>
                  </div>
                  <div className="text-xs txt-secondary border border-[var(--border)] p-3">
                    {pcr.interpretation}
                  </div>
                </>
              )}
            </div>

            {/* Max Pain */}
            <div className="panel p-5">
              <div className="overline mb-4">Max Pain · {symbol}</div>
              {!maxPain ? <div className="txt-muted text-sm">Loading…</div> : (
                <>
                  <div className="flex items-end gap-3 mb-4">
                    <div className="font-display text-5xl font-bold">₹{fmt(maxPain.max_pain_strike, 0)}</div>
                    <div className={`mb-1 text-sm font-mono-data ${maxPain.distance_from_spot_pct >= 0 ? "txt-profit" : "txt-loss"}`}>
                      {maxPain.distance_from_spot_pct >= 0 ? "+" : ""}{maxPain.distance_from_spot_pct}%
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs font-mono-data mb-4">
                    <div><div className="overline">Spot</div><div className="text-white mt-1">₹{fmt(maxPain.spot, 0)}</div></div>
                    <div><div className="overline">ATM</div><div className="text-white mt-1">₹{fmt(maxPain.atm, 0)}</div></div>
                  </div>
                  <div className="text-xs txt-secondary border border-[var(--border)] p-3 mb-3">
                    {maxPain.interpretation}
                  </div>
                  <div className="overline mb-2">Writer loss by strike (₹ Cr)</div>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {(maxPain.pain_table || []).slice(0, 8).map((row) => (
                      <div key={row.strike} className="flex items-center justify-between text-xs font-mono-data">
                        <span className={row.strike === maxPain.max_pain_strike ? "text-yellow-400 font-bold" : "txt-secondary"}>
                          {row.strike === maxPain.max_pain_strike ? "★ " : ""}{row.strike}
                        </span>
                        <span className="txt-muted">₹{row.writer_loss_cr}Cr</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* GREEKS */}
        {tab === "greeks" && (
          <div className="space-y-4">
            <div className="panel p-5">
              <div className="overline mb-4">Options Chain Greeks · {symbol}</div>
              {chainLoading && <div className="txt-muted text-sm">Loading…</div>}
              {chain && <GreeksTable chain={chain} />}
            </div>

            {portfolioGreeks && (
              <div className="panel p-5">
                <div className="overline mb-3">ATM Straddle Greeks (sample)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono-data">
                  {[
                    { label: "Delta Δ", value: portfolioGreeks.delta?.toFixed(3), color: "txt-profit" },
                    { label: "Gamma Γ", value: portfolioGreeks.gamma?.toFixed(4), color: "txt-secondary" },
                    { label: "Theta Θ", value: portfolioGreeks.theta?.toFixed(2), color: "txt-loss" },
                    { label: "Vega ν", value: portfolioGreeks.vega?.toFixed(3), color: "text-blue-400" },
                  ].map(({ label, value, color }) => (
                    <div key={label}>
                      <div className="overline">{label}</div>
                      <div className={`font-display text-3xl font-bold mt-1 ${color}`}>{value ?? "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
